const MATCH_TYPE_LABELS = {
  single: '1v1',
  triple_threat: 'Triple Threat',
  fatal_4_way: 'Fatal 4 Way',
  six_pack_challenge: '6-Pack Challenge',
  battle_royal: 'Battle Royal',
};

const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const endOfDay = (date) => {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
};

export const toMatchDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  return new Date(value);
};

export const normalizeMatchParticipants = (rawMatch = {}) => {
  if (Array.isArray(rawMatch.participants) && rawMatch.participants.length > 0) {
    return rawMatch.participants;
  }

  const legacyParticipants = [];
  if (rawMatch.factionAId) {
    legacyParticipants.push({
      factionId: rawMatch.factionAId,
      factionName: rawMatch.factionAName,
      ownerUid: rawMatch.factionAOwnerUid || null,
    });
  }
  if (rawMatch.factionBId) {
    legacyParticipants.push({
      factionId: rawMatch.factionBId,
      factionName: rawMatch.factionBName,
      ownerUid: rawMatch.factionBOwnerUid || null,
    });
  }

  return legacyParticipants;
};

export const normalizeGmMatch = (docSnapOrObject) => {
  const raw = 'data' in docSnapOrObject ? docSnapOrObject.data() : docSnapOrObject;
  const id = 'id' in docSnapOrObject ? docSnapOrObject.id : raw.id;
  const participants = normalizeMatchParticipants(raw);

  return {
    id,
    ...raw,
    participants,
    matchType: raw.matchType || (participants.length >= 3 ? 'triple_threat' : 'single'),
    kickoffAt: toMatchDate(raw.kickoffAt || raw.createdAt),
    weekStart: toMatchDate(raw.weekStart),
    weekEnd: toMatchDate(raw.weekEnd),
    createdAt: toMatchDate(raw.createdAt),
    updatedAt: toMatchDate(raw.updatedAt),
    closedAt: toMatchDate(raw.closedAt),
  };
};

export const getGmMatchLabel = (matchType) => MATCH_TYPE_LABELS[matchType] || MATCH_TYPE_LABELS.single;

export const isFactionInGmMatch = (match, factionId) =>
  Boolean(factionId && match?.participants?.some((participant) => participant.factionId === factionId));

export const getFactionGmMatches = (matches, factionId) =>
  [...(matches || [])]
    .filter((match) => isFactionInGmMatch(match, factionId))
    .sort((a, b) => {
      const aTime = a.kickoffAt?.getTime?.() || a.createdAt?.getTime?.() || 0;
      const bTime = b.kickoffAt?.getTime?.() || b.createdAt?.getTime?.() || 0;
      return bTime - aTime;
    });

export const formatGmMatchDate = (date) =>
  date && !Number.isNaN(date.getTime?.())
    ? date.toLocaleDateString('it-IT')
    : '-';

export const formatGmMatchParticipants = (match, currentFactionId = null) =>
  (match?.participants || [])
    .map((participant) =>
      participant.factionId === currentFactionId
        ? `${participant.factionName || 'La tua fazione'}`
        : participant.factionName || 'Fazione'
    )
    .join(' vs ');

export const calculateGmMatchPointsByFaction = (matches = []) =>
  matches.reduce((scores, match) => {
    if (match.status !== 'closed') return scores;

    if (Array.isArray(match.results) && match.results.length > 0) {
      match.results.forEach((result) => {
        if (!result?.factionId) return;
        scores[result.factionId] = (scores[result.factionId] || 0) + (result.matchPoints || 0);
      });
      return scores;
    }

    if (match.winnerId) {
      scores[match.winnerId] = (scores[match.winnerId] || 0) + (match.participants?.length || 1);
    }

    return scores;
  }, {});

export const getGmMatchLifecycle = (match, referenceDate = new Date()) => {
  if (!match) return 'closed';
  if (match.status === 'closed' || match.closedAt) return 'closed';

  const reference = toMatchDate(referenceDate) || new Date();
  const weekStart = toMatchDate(match.weekStart);
  const weekEnd = toMatchDate(match.weekEnd);

  if (weekStart && reference.getTime() < startOfDay(weekStart).getTime()) {
    return 'upcoming';
  }

  if (weekEnd && reference.getTime() > endOfDay(weekEnd).getTime()) {
    return 'expired';
  }

  if (
    weekStart &&
    weekEnd &&
    reference.getTime() >= startOfDay(weekStart).getTime() &&
    reference.getTime() <= endOfDay(weekEnd).getTime()
  ) {
    return 'active';
  }

  return match.status === 'open' ? 'active' : 'closed';
};

export const isGmMatchInProgress = (match, referenceDate = new Date()) =>
  getGmMatchLifecycle(match, referenceDate) === 'active';
